import { checkIfCupsIsValid } from '@modules/cups/application/check-if-cups-is-valid'
import { getPotentialByCups } from '@modules/potential/application/get-potential-by-cups'
import { getSupplyPointByCups } from '@modules/supply-point/application/get-supply-point-by-cups'
import { checkIsNewContractOrUpselling } from '@modules/electricity-contract/application/check-is-new-contract-or-upselling-by-cups'
import { getDealStage as getDealStageUseCase } from '@modules/deal/application/get-deal-stage'
import { updateAddressOnClips } from '@modules/update-address/application/update-address-on-clips'
import { sendDatadogError, sendDatadogEvent } from '@ui/plugins/datadog'
import { modifyConsumption } from '@modules/modify-consumption/application/modify-consumption'
import { ConsumptionStatus } from '@modules/supply-point/domain/supply-point.types'

function validateCups({ commit, dispatch }, cups) {
  dispatch('resetInfoState')

  function onValidCups() {
    sendDatadogEvent({
      name: 'valid-cups',
      value: { cups },
    })
    commit('SET_CUPS', cups)
  }

  function onInvalidCups() {
    sendDatadogError({
      name: 'invalid-cups',
      options: { cups },
    })
    throw new Error('invalid-cups')
  }

  checkIfCupsIsValid().execute(
    {
      cups,
    },
    {
      onValidCups,
      onInvalidCups,
    }
  )
}
function updateCups({ commit }, cups) {
  commit('SET_CUPS', cups)
}

async function checkIfLeadIsNewContractOrUpselling({ commit }, cups) {
  function onUpselling(contractId) {
    commit('SET_ELECTRICITY_CONTRACT_ID', contractId)
  }

  function onNetworkError() {
    throw new Error('api-error')
  }

  await checkIsNewContractOrUpselling().execute(
    { cups },
    {
      onNewContract: () => {},
      onUpselling,
      onNetworkError,
    }
  )
}

async function requestClient({ commit, rootGetters }, cups) {
  const isExternalChannel = rootGetters['user/isExternalChannel']

  function onPotential(client) {
    commit('SET_CLIENT', client)
  }

  function onNoPotential() {
    // Never keep the contact of a previously loaded potential
    commit('RESET_CLIENT')
  }

  function onNetworkError() {
    throw new Error('api-error')
  }

  await getPotentialByCups().execute(
    { cups, isExternalChannel },
    {
      onPotential,
      onNoPotential,
      onExternalChannel: () => {},
      onNetworkError,
    }
  )
}

async function getSupplyPoint({ commit, dispatch, state }, cups) {
  function onSupplyPoint(supplyPoint) {
    commit('SET_SUPPLYPOINT', supplyPoint)

    if (
      state.supplyPoint?.consumptionStatus === ConsumptionStatus.OK &&
      state.intervalId
    ) {
      dispatch('stopConsumptionPolling')
    }
  }

  function onNetworkError() {
    commit('SET_ERROR_IN_CONSUMPTION_POLLING', true)
    if (state.intervalId) {
      dispatch('stopConsumptionPolling')
    }
    throw new Error('api-error')
  }

  await getSupplyPointByCups().execute(
    { cups },
    {
      onSupplyPoint,
      onNetworkError,
    }
  )
}

function startConsumptionPolling({ commit, dispatch }, cups) {
  commit('SET_ERROR_IN_CONSUMPTION_POLLING', false)
  const intervalId = setInterval(() => dispatch('getSupplyPoint', cups), 10000)
  commit('SET_INTERVAL_ID', intervalId)
}

function stopConsumptionPolling({ commit, state }) {
  clearInterval(state.intervalId)
  commit('SET_INTERVAL_ID', null)
  if (!state.errorInConsumptionPolling) {
    commit('SET_CONSUMPTION_UPDATED', true)
  }
}

async function requestSupplyPoint({ state, dispatch, commit }, cups) {
  commit('SET_CONSUMPTION_UPDATED', false)
  await dispatch('getSupplyPoint', cups)

  if (state?.supplyPoint?.consumptionStatus === ConsumptionStatus.OK) return
  dispatch('startConsumptionPolling', cups)
}

function resetInfoState({ commit }) {
  commit('RESET_STATE')
}

async function getDealStage({ commit }, cups) {
  commit('SET_DEAL_STAGE', '')
  commit('SET_INSTALLATION_STAGE', '')

  function onOfferStage(status, stage = '') {
    commit('SET_DEAL_STAGE', status)
    if (stage) {
      commit('SET_INSTALLATION_STAGE', stage)
    }
  }

  await getDealStageUseCase().execute(
    { cups },
    {
      onOfferStage,
      onError: () => {},
    }
  )
}

async function requestUpdateAddressOnClips(
  { state, commit },
  { postalCode, city, address, province }
) {
  function onAddressUpdated() {
    const dataAddress = {
      address,
      city,
      postalCode,
      province,
    }
    sendDatadogEvent({
      name: 'modify-address',
      value: { cups: state.cups, ...dataAddress },
    })
    commit('SET_SUPPLYPOINT_ADDRESS', dataAddress)
  }
  function onNetworkError(message) {
    sendDatadogError({
      name: 'modify-address-error',
      options: { cups: state.cups, message },
    })
    throw new Error(message)
  }

  await updateAddressOnClips().execute(
    {
      cups: state.cups,
      postalCode,
      locality: city,
      street: address,
    },
    { onAddressUpdated, onNetworkError }
  )
}

async function requestModifyConsumption(
  { state, commit },
  { previousAnnualConsumption, newConsumptions, reason }
) {
  function onConsumptionModified() {
    const dataConsumption = {
      previousAnnualConsumption,
      newConsumptions,
      reason,
    }
    sendDatadogEvent({
      name: 'modify-consumption',
      value: { cups: state.cups, ...dataConsumption },
    })
    commit('SET_SUPPLYPOINT_CONSUMPTION', dataConsumption)
  }
  function onNetworkError(message) {
    sendDatadogError({
      name: 'modify-consumption-error',
      options: { cups: state.cups, message },
    })
    throw new Error(message)
  }

  await modifyConsumption().execute(
    {
      cups: state.cups,
      previousAnnualConsumption,
      newConsumptions,
      reason,
    },
    { onConsumptionModified, onNetworkError }
  )
}

function setOverwrittenCups({ commit }, overwritten) {
  commit('SET_OVERWRITTEN_CUPS', overwritten)
}

export default {
  validateCups,
  updateCups,
  checkIfLeadIsNewContractOrUpselling,
  requestClient,
  getSupplyPoint,
  requestSupplyPoint,
  startConsumptionPolling,
  stopConsumptionPolling,
  resetInfoState,
  getDealStage,
  requestUpdateAddressOnClips,
  requestModifyConsumption,
  setOverwrittenCups,
}
